import React, { useMemo } from 'react'
import Seo from '../Seo'

const env = (typeof process !== 'undefined' && process.env) || {}

const DEFAULTS = {
  embedUrl: 'https://app.leanit101.com/benjamin',
  embedToken:
    'eyJvcmdhbml6YXRpb25JZCI6NDEsImFnZW50U2x1ZyI6ImNmby1kZW1vLWFzc2lzdGFudCIsInRlbmFudFNsdWciOiJkb24tdm9zcyIsImFzc2lzdGFudE5hbWUiOiJDaHJpc3RvcGhlciIsIm1ldGFkYXRhIjp7ImNoYW5uZWxEZWZhdWx0Ijoid2ViX2VtYmVkIn19.7933043852e2cd210dba157565a1e86a7b5a73ce5df8f9782a57b526b8c506ff',
  agentSlug: 'cfo-demo-assistant',
  tenantSlug: 'don-voss',
  organizationId: '41',
  assistantName: 'Christopher',
  channel: 'web_embed',
  utmSource: 'leanit101.com',
  utmCampaign: 'cfo-demo-poc',
}

const SUGGESTED_QUESTIONS = [
  'Summarize Sempra’s financial health in CFO language.',
  'What changed in FY2025 compared with FY2024?',
  'Did revenue grow, and did profitability keep pace?',
  'What would concern a CFO most in this dataset?',
  'Is liquidity a concern based on the available figures?',
  'Separate facts from inferences in your assessment.',
]

const CFO_FOCUS_AREAS = [
  'Growth vs profitability trade-off',
  'Cash generation and operating cash flow',
  'Leverage, debt load, and liquidity',
  'What deserves management attention next',
]

const CfoDemoPage = () => {
  const queryParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams('')
    return new URLSearchParams(window.location.search || '')
  }, [])

  const embedToken =
    queryParams.get('token') ||
    env.REACT_APP_CFO_DEMO_EMBED_TOKEN ||
    DEFAULTS.embedToken

  const agentSlug =
    queryParams.get('agentSlug') ||
    env.REACT_APP_CFO_DEMO_AGENT_SLUG ||
    DEFAULTS.agentSlug

  const tenantSlug =
    (
      queryParams.get('tenantSlug') ||
      env.REACT_APP_CFO_DEMO_TENANT_SLUG ||
      DEFAULTS.tenantSlug
    )
      .toString()
      .trim()
      .toLowerCase()

  const organizationId =
    queryParams.get('organizationId') ||
    env.REACT_APP_CFO_DEMO_ORGANIZATION_ID ||
    DEFAULTS.organizationId

  const assistantName =
    queryParams.get('assistantName') ||
    env.REACT_APP_CFO_DEMO_ASSISTANT_NAME ||
    DEFAULTS.assistantName

  const channel = queryParams.get('channel') || DEFAULTS.channel
  const utmSource = queryParams.get('utm_source') || DEFAULTS.utmSource
  const utmCampaign = queryParams.get('utm_campaign') || DEFAULTS.utmCampaign

  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams()
    if (embedToken) params.set('token', embedToken)
    params.set('agentSlug', agentSlug)
    params.set('tenantSlug', tenantSlug)
    params.set('organizationId', organizationId)
    params.set('assistantName', assistantName)
    params.set('channel', channel)
    params.set('locale', 'en-US')
    if (utmSource) params.set('utm_source', utmSource)
    if (utmCampaign) params.set('utm_campaign', utmCampaign)
    params.set('meta.source', 'cfo-demo-poc')
    params.set('meta.partner', 'don-voss')

    const baseUrl = env.REACT_APP_CFO_DEMO_EMBED_URL || DEFAULTS.embedUrl
    return `${baseUrl}?${params.toString()}`
  }, [
    agentSlug,
    assistantName,
    channel,
    embedToken,
    organizationId,
    tenantSlug,
    utmCampaign,
    utmSource,
  ])

  return (
    <section className="relative flex min-h-screen w-full flex-col overflow-hidden bg-[#071019] text-white">
      <Seo
        title="CFO Demo | Christopher"
        description="Private CFO-facing demo page for Christopher, showing conversational financial analysis on top of curated public-company data."
        path="/cfo-demo"
        noindex
      />

      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,#0b1322_0%,#071019_48%,#060c15_100%)]" />
        <div
          className="absolute -top-32 left-[8%] h-[560px] w-[560px] rounded-full opacity-30"
          style={{
            background:
              'radial-gradient(50% 50% at 50% 50%, rgba(56,189,248,0.25) 0%, rgba(56,189,248,0) 70%)',
            filter: 'blur(56px)',
          }}
        />
        <div
          className="absolute bottom-[-220px] right-[-120px] h-[620px] w-[620px] rounded-full opacity-24"
          style={{
            background:
              'radial-gradient(50% 50% at 50% 50%, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0) 68%)',
            filter: 'blur(52px)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-12 md:py-16">
        <header className="space-y-5 text-center">
          <p className="text-xs uppercase tracking-[0.42em] text-sky-200/70">
            CFO Demo
          </p>
          <h1 className="text-3xl font-semibold tracking-tight md:text-5xl">
            Christopher • conversational finance support for executives
          </h1>
          <p className="mx-auto max-w-4xl text-base text-slate-200 md:text-lg">
            Private POC page for a CFO-oriented demo. Christopher answers
            finance questions in executive language using a curated knowledge
            base built from public SEC data for Sempra as a stand-in for what,
            in production, would ideally connect to ERP, FP&amp;A, BI, treasury,
            and reporting systems.
          </p>
        </header>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_380px]">
          <div className="overflow-hidden rounded-[30px] border border-sky-100/10 bg-slate-900/55 shadow-2xl">
            <div className="border-b border-white/8 px-5 py-4">
              <div className="text-[11px] uppercase tracking-[0.34em] text-sky-200/68">
                Live demo
              </div>
              <div className="mt-2 text-sm text-slate-300">
                Designed to answer quickly, separate fact from inference, and
                reduce search-and-synthesis time for finance leaders.
              </div>
            </div>
            <div className="relative h-[560px] w-full md:h-[700px]">
              <iframe
                title="Christopher — CFO Demo"
                src={iframeSrc}
                allow="clipboard-read; clipboard-write"
                className="absolute inset-0 h-full w-full"
                style={{ background: 'transparent', border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="grid content-start gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <div className="text-[11px] uppercase tracking-[0.3em] text-sky-200/72">
                Demo framing
              </div>
              <h2 className="mt-3 text-xl font-semibold">
                Executive answers, not dashboard hunting
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-200">
                This POC is built to show how Benjamin can sit on top of finance
                data and answer in CFO language: concise, analytical, grounded
                in the available materials, and explicit about uncertainty.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <div className="text-[11px] uppercase tracking-[0.3em] text-sky-200/72">
                What this demo covers
              </div>
              <ul className="mt-3 space-y-3 text-sm leading-6 text-slate-200">
                {CFO_FOCUS_AREAS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-amber-300/20 bg-[#0d1322] p-5">
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-200/78">
                Suggested questions
              </div>
              <ul className="mt-3 space-y-3 text-sm leading-6 text-slate-200">
                {SUGGESTED_QUESTIONS.map((question) => (
                  <li key={question}>{question}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <div className="text-[11px] uppercase tracking-[0.3em] text-sky-200/72">
                Safe limitation
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-200">
                This is a simulation using curated public-company financial
                data. It is not a live ERP, FP&amp;A, treasury, or BI integration,
                and it should not be presented as private internal company data.
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-300/20 bg-[#0d1322] p-5">
              <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-200/78">
                Shareable link
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-200">
                Use this page for the client-facing POC:
              </p>
              <code className="mt-3 block break-all text-xs text-emerald-200 md:text-sm">
                https://leanit101.com/cfo-demo
              </code>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-white/10 bg-slate-900/45 p-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              FY2025 revenue
            </div>
            <div className="mt-3 text-3xl font-semibold">$13.70B</div>
            <div className="mt-2 text-sm text-slate-300">
              Up 3.9% versus FY2024
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/45 p-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Net income
            </div>
            <div className="mt-3 text-3xl font-semibold">$1.84B</div>
            <div className="mt-2 text-sm text-slate-300">
              Down 35.8% year over year
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/45 p-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Operating cash flow
            </div>
            <div className="mt-3 text-3xl font-semibold">$4.57B</div>
            <div className="mt-2 text-sm text-slate-300">
              Down 7.0% versus FY2024
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/45 p-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Long-term debt / equity
            </div>
            <div className="mt-3 text-3xl font-semibold">0.92x</div>
            <div className="mt-2 text-sm text-slate-300">
              Simple leverage framing from the demo pack
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CfoDemoPage
